/**
 * Wing Chun (WingChunStore.swift): prácticas cronometradas o marcadas sin cronómetro, mínimo diario, forma del día
 * por rotación. Independiente de Fuerza: el flujo de Fuerza solo LANZA la práctica a través de aquí.
 * La rotación se guarda en Settings (`WINGCHUN_ROTATION`) para que sea la misma en todos los dispositivos.
 */
import {
  averageDuration, dateKey, eligibleForms, habitStats, isEligibleForMinimum, lastPracticedByForm, minimumDays,
  parseRotation, suggestedForm,
} from '@sut/domain';
import { newId, type WingChunPractice, type Workbook } from '@sut/workbook';

export type WingChunSource = 'strengthFlow' | 'wingChunCard' | 'extraPractice';
export const OUTCOMES = [
  { id: 'noSalio', label: 'No salió' },
  { id: 'casi', label: 'Casi' },
  { id: 'salio', label: 'Salió' },
] as const;

export const ROTATION_KEY = 'WINGCHUN_ROTATION';

export const rotationOf = (wb: Workbook | null) => parseRotation(wb?.settings[ROTATION_KEY]);

export function wingChunToday(wb: Workbook | null, today = dateKey()) {
  const all = wb?.wingChun ?? [];
  const todays = all.filter(p => p.date === today).sort((a, b) => (a.completedAt < b.completedAt ? -1 : 1));
  const rotation = rotationOf(wb);
  const minimum = todays.find(p => p.isDailyMinimum) ?? null;
  const extras = [...new Set(todays.filter(p => !p.isDailyMinimum).map(p => p.form))];
  return {
    rotation,
    eligible: eligibleForms(rotation),
    isDone: !!minimum,
    minimumForm: minimum?.form ?? null,
    extras,
    countToday: todays.length,
    suggested: suggestedForm(rotation, lastPracticedByForm(all)),
    average: (form: string) => averageDuration(all, form),
    stats: () => habitStats(minimumDays(all), today),
  };
}

export type PracticeResult = { kind: 'minimum'; streak: number } | { kind: 'extra'; count: number };

/** Nueva práctica. Solo la primera práctica válida del día cuenta como mínimo. */
export function newPractice(wb: Workbook | null, form: string, source: WingChunSource, startedAt: Date | null, durationSeconds: number | null):
  { practice: WingChunPractice; result: PracticeResult } {
  const now = new Date();
  const today = wingChunToday(wb, dateKey(now));
  const isMinimum = isEligibleForMinimum(form, today.rotation) && !today.isDone;
  const practice: WingChunPractice = {
    id: newId(), date: dateKey(now), form, startedAt: startedAt?.toISOString() ?? '', completedAt: now.toISOString(),
    durationSeconds: durationSeconds === null ? null : Math.max(0, Math.round(durationSeconds)), isDailyMinimum: isMinimum,
    source, outcome: '', note: '', createdAt: now.toISOString(),
  };
  const days = minimumDays([...(wb?.wingChun ?? []), practice]);
  return {
    practice,
    result: isMinimum ? { kind: 'minimum', streak: habitStats(days, practice.date).currentStreak } : { kind: 'extra', count: today.countToday + 1 },
  };
}
